'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { useRouter } from 'next/navigation';
import { Tv2, Radio, Calendar, Globe, ChevronLeft, ChevronRight, Newspaper } from 'lucide-react';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  href: string;
  badge?: string;
  group?: string;
}

const navItems: NavItem[] = [
  { label: 'Live TV', icon: <Tv2 size={18} />, href: '/streaming-dashboard#live-now', badge: '47', group: 'discover' },
  { label: 'Radio', icon: <Radio size={18} />, href: '/streaming-dashboard#radio', group: 'discover' },
  { label: 'Live Events', icon: <Calendar size={18} />, href: '/streaming-dashboard#events', badge: '3', group: 'discover' },
  { label: 'Countries', icon: <Globe size={18} />, href: '/streaming-dashboard#countries', group: 'discover' },
  { label: 'News', icon: <Newspaper size={18} />, href: '/news', group: 'discover' },
];

const groupLabels: Record<string, string> = {
  discover: 'DISCOVER',
};

interface SidebarProps {
  currentPath: string;
}

export default function Sidebar({ currentPath }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const router = useRouter();

  return (
    <aside
      className={`hidden lg:flex flex-col bg-black border-r border-[rgb(38,38,38)] transition-all duration-300 ease-in-out flex-shrink-0 ${collapsed ? 'w-16' : 'w-56'}`}
    >
      {/* Logo — clickable to home */}
      <div
        className={`flex items-center border-b border-[rgb(38,38,38)] h-14 px-3 cursor-pointer hover:opacity-80 transition-opacity ${collapsed ? 'justify-center' : 'gap-2'}`}
        onClick={() => router.push('/')}
        role="button"
        aria-label="Go to home page"
      >
        <AppLogo size={28} />
        {!collapsed && (
          <span className="font-bold text-sm text-white tracking-tight leading-tight">
            GoLive<span className="text-red-600">Africa</span>
          </span>
        )}
      </div>

      {/* Nav Groups */}
      <nav className="flex-1 overflow-y-auto py-3 scrollbar-hide">
        {['discover'].map((group) => {
          const items = navItems.filter((n) => n.group === group);
          if (items.length === 0) return null;
          return (
            <div key={`group-${group}`} className="mb-4">
              {!collapsed && (
                <p className="px-3 mb-1 text-[10px] font-600 text-[rgb(115,115,115)] tracking-widest uppercase">
                  {groupLabels[group]}
                </p>
              )}
              {items.map((item) => {
                const hasQuery = item.href.includes('?');
                const isActive = hasQuery
                  ? currentPath === item.href
                  : currentPath === item.href || (item.href !== '/' && currentPath.startsWith(item.href.split('#')[0]) && item.href.split('#')[0] !== '/');
                return (
                  <Link
                    key={`nav-${item.href}-${item.label}`}
                    href={item.href}
                    className={`flex items-center gap-3 mx-2 px-2 py-2 rounded-lg text-sm transition-all duration-150 group relative ${
                      isActive
                        ? 'bg-[rgba(220,38,38,0.15)] text-red-500'
                        : 'text-[rgb(163,163,163)] hover:bg-[rgb(24,24,24)] hover:text-white'
                    } ${collapsed ? 'justify-center' : ''}`}
                  >
                    <span className="flex-shrink-0">{item.icon}</span>
                    {!collapsed && <span className="font-medium truncate">{item.label}</span>}
                    {!collapsed && item.badge && (
                      <span className="ml-auto bg-red-600 text-white text-[10px] font-700 px-1.5 py-0.5 rounded-full tabular-nums">
                        {item.badge}
                      </span>
                    )}
                    {collapsed && (
                      <div className="absolute left-full ml-2 px-2 py-1 bg-[rgb(24,24,24)] border border-[rgb(38,38,38)] text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
                        {item.label}
                        {item.badge && <span className="ml-1 text-red-500">({item.badge})</span>}
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Collapse Toggle */}
      <div className="border-t border-[rgb(38,38,38)] p-2">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`flex items-center gap-2 w-full px-2 py-2 rounded-lg text-[rgb(115,115,115)] hover:text-white hover:bg-[rgb(24,24,24)] transition-all duration-150 text-sm ${collapsed ? 'justify-center' : ''}`}
        >
          {collapsed ? <ChevronRight size={16} /> : <><ChevronLeft size={16} /><span className="font-medium">Collapse</span></>}
        </button>
      </div>
    </aside>
  );
}